import type { ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, ic } from "../wizard-icons";
import type { Phase } from "../wizard-data";

const K = keys.quickSetup;

/**
 * Sticky mobile header: left = Trioty logo (step 1) or "‹ Back" (steps 2+),
 * right = locale pill; below = "Step X of 5 · {phase label}" + a slim gold
 * progress bar. `topLevelIndex` is 0-based into `phases`.
 */
export function MobileHeader({
  topLevelIndex,
  done,
  phases,
  onBack,
  showBack,
  onSwitchLocale,
  activeLocale,
}: {
  topLevelIndex: number;
  done: boolean;
  phases: Phase[];
  onBack?: () => void;
  showBack?: boolean;
  onSwitchLocale: () => void;
  activeLocale: string;
}): ReactElement {
  const t = useTranslations();
  const dir = activeLocale === "ar" ? "rtl" : "ltr";
  const total = phases.length;
  const curIdx = done ? total : topLevelIndex;
  const phaseLabel = phases[Math.min(curIdx, total - 1)]?.name ?? "";
  const pct = Math.min(100, Math.max(0, ((curIdx + 1) / total) * 100));

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        flexShrink: 0,
        padding: "20px 20px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        borderBottom: "1px solid var(--divider)",
        background: "var(--surface)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {showBack && onBack ? (
          <button
            onClick={onBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "transparent",
              border: "none",
              color: "var(--gold-ink)",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              padding: 0,
            }}
          >
            <span style={{ display: "flex", transform: dir === "rtl" ? "scaleX(-1)" : undefined }}>
              {ic(Icons.back, 14)}
            </span>
            {t(K.header.back)}
          </button>
        ) : (
          <img
            src="/logo-sidebar.svg"
            alt={t(K.brand.name)}
            style={{ height: 24, width: "auto", display: "block" }}
          />
        )}
        <button
          onClick={onSwitchLocale}
          aria-label={activeLocale === "ar" ? "English" : "العربية"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 999,
            background: "var(--surface)",
            border: "1px solid var(--border-strong)",
            color: "var(--navy)",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {activeLocale === "ar" ? "English" : "العربية"}
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--fine)" }}>
          {t(K.header.stepOf, { current: Math.min(curIdx + 1, total), total, label: phaseLabel })}
        </span>
        <div
          style={{
            display: "flex",
            height: 5,
            borderRadius: 999,
            background: "var(--divider)",
            overflow: "hidden",
          }}
        >
          <span
            style={{
              width: `${pct}%`,
              background: "var(--accent)",
              borderRadius: 999,
              transition: "width .2s ease",
            }}
          />
        </div>
      </div>
    </div>
  );
}
